"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/casting/High-Pressure-Die-Casting/Quality-Assurance";

const QC_IMAGES = [
	`${IMAGE_BASE}/measuring-machine-1.webp`,
	`${IMAGE_BASE}/cmm.webp`,
	`${IMAGE_BASE}/contour-measuring-instrument-8.jpg`,
	`${IMAGE_BASE}/altimeter.webp`,
] as const;

const ISO_LOGOS = [
	{ src: `${IMAGE_BASE}/iso-9001.png`, altKey: "qaIso9001Alt" },
	{ src: `${IMAGE_BASE}/iso-13485.png`, altKey: "qaIso13485Alt" },
	{ src: `${IMAGE_BASE}/iso-14001.png`, altKey: "qaIso14001Alt" },
	{ src: `${IMAGE_BASE}/iatf.png`, altKey: "qaIatfAlt" },
] as const;

const CONTENT_BLOCKS = [
	{ titleKey: "qaCoreAdvantagesTitle", descKey: "qaCoreAdvantagesDesc" },
	{ titleKey: "qaStandardsTitle", descKey: "qaStandardsDesc" },
	{ titleKey: "qaInspectionTitle", descKey: "qaInspectionDesc" },
] as const;

export default function QualityAssurance() {
	const t = useTranslations("new-high-pressure-casting");

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("qaTitle")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-black sm:mt-4 md:text-base">{t("qaDesc")}</p>
				</header>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-5">
					<div className="lg:col-span-2">
						<div className="grid grid-cols-2 gap-3 sm:gap-4">
							{QC_IMAGES.map((src, index) => (
								<div key={src} className="relative aspect-[4/3] overflow-hidden bg-white">
									<Image
										src={src}
										alt={t(`qaImage${index + 1}Alt` as any)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							))}
						</div>

						<div className="mt-6 flex items-center justify-between gap-6 sm:mt-8 sm:gap-8 md:gap-10">
							{ISO_LOGOS.map((logo) => (
								<div key={logo.altKey} className="relative h-16 w-16 sm:h-24 sm:w-24">
									<Image src={logo.src} alt={t(logo.altKey)} fill className="object-contain" sizes="80px" />
								</div>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-6 sm:gap-8 lg:col-span-3">
						{CONTENT_BLOCKS.map((block) => (
							<div key={block.titleKey}>
								<h3 className="mb-2 text-base font-bold text-themePrimary400 sm:mb-3 sm:text-lg md:text-xl">
									{t(block.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(block.descKey)}</p>
							</div>
						))}

						<p className="text-sm leading-relaxed text-black md:text-base">{t("qaCtaText")}</p>

						<div>
							<Link
								href="/upload"
								className="inline-block rounded-lg bg-themePrimary400 px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-themePrimary400/85 sm:text-base"
							>
								{t("qaCtaButton")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
